import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

/**
 * JtButtonComponent — Bootstrap button abstraction.
 *
 * Absorbs all btn-* classes. Zero Bootstrap in consuming templates.
 *
 * Usage:
 *   <jt-button variant="primary" size="sm" icon="save"
 *              data-testid="btn-save" (byClick)="save()">
 *     Save
 *   </jt-button>
 *
 *   <jt-button variant="danger" (byClick)="delete()">Delete</jt-button>
 *   <jt-button variant="secondary" [disabled]="true">Cancel</jt-button>
 *
 * Bootstrap classes are ONLY inside this template.
 * Note: BS4 and BS5 btn-* classes are identical — no change needed on BS5 upgrade.
 */
@Component({
  selector: 'jt-button',
  templateUrl: './jt-button.component.html',
  styleUrls: ['./jt-button.component.css'],
  standalone: true,
  imports: [TranslateModule, TooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class JtButtonComponent {


  @Input() variant: 'primary' | 'secondary' | 'success' | 'danger' | 'warning'
    | 'info' | 'light' | 'dark' | 'link' | 'outline-primary'
    | 'outline-secondary' | 'outline-danger' | 'outline-success' = 'primary';

  @Input() size: 'sm' | 'lg' | 'default' = 'default';
  @Input() icon: string = '';
  @Input() iconPosition: 'start' | 'end' = 'start';

  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() disabled: boolean = false;
  @Input() loading: boolean = false;
  @Input() tooltip: string = '';

  /**
   * Accessible label for the button — use when the button label is icon-only
   * or the ng-content text is not descriptive enough for screen readers.
   * Translation key or plain text.
   * Example: <jt-button icon="trash" ariaLabel="Common.DeleteRecord">
   */
  @Input() ariaLabel: string = '';

  /**
   * ID of an element that labels this button (aria-labelledby).
   * Use when the button label text comes from another element on page.
   */
  @Input() ariaLabelledBy: string = '';

  @Output() byClick = new EventEmitter<MouseEvent>();

  get buttonClass(): string {
    const classes = ['btn', `btn-${this.variant}`];
    if (this.size !== 'default') classes.push(`btn-${this.size}`);
    return classes.join(' ');
  }

  onClick(event: MouseEvent): void {
    if (!this.disabled && !this.loading) {
      this.byClick.emit(event);
    }
  }
}
